import { AxiosError } from 'axios';
import { useRef, useState } from 'react';

import {
	Box,
	Button,
	CloseButton,
	Container,
	FileButton,
	Flex,
	Group,
	Image,
	ScrollArea,
	Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';

import { useMutation } from '@tanstack/react-query';

import { selectUsername } from '@/store/user-process/user-selectors';

import { bookingApi } from '@/shared/api/booking';
import { compressImage } from '@/shared/helpers/compressImage';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction';
import { IRequestError } from '@/shared/types/app.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { LabelInput } from '@/shared/uikit/components/input';
import { CarInput } from '@/shared/uikit/components/input/CarInput/CarInput';

import { IconPhoto } from '@tabler/icons-react';

import { PlaceTakenSelection } from './types';

import { useAppSelector } from '@/hooks';

const SUBJECT =
	'На закреплённом/забронированном парковочном месте находится посторонний автомобиль';

type PlaceTakenFormProps = {
	opened: boolean;
	selection: PlaceTakenSelection;
	onBack: () => void;
	onClose: () => void;
};

type TPlaceTakenFormData = {
	subject: string;
	carNumber: string;
};

interface PreviewType {
	img: string;
	lastModified: number;
	name: string;
}

export const PlaceTakenForm = ({ opened, selection, onBack, onClose }: PlaceTakenFormProps) => {
	const fullname = useAppSelector(selectUsername);

	const form = useForm<TPlaceTakenFormData>({
		initialValues: {
			subject: SUBJECT,
			carNumber: '',
		},
		validate: {
			carNumber: (value) => (!value.trim() ? 'Укажите номер автомобиля' : null),
		},
	});

	const [files, setFiles] = useState<File[]>([]);
	const [previews, setPreviews] = useState<PreviewType[]>([]);
	const resetRef = useRef<() => void>(null);

	const showPreviews = (nextFiles: File[]) => {
		setPreviews(
			nextFiles.map((file) => ({
				img: URL.createObjectURL(file),
				lastModified: file.lastModified,
				name: file.name,
			})),
		);
	};

	const removeImage = (lastModified: number) => {
		const filtered = files.filter((file) => file.lastModified !== lastModified);
		setFiles(filtered);
		showPreviews(filtered);
	};

	const changeHandler = async (selectedFiles: File[]) => {
		resetRef.current?.();

		const fresh = selectedFiles.filter(
			(file) => !files.some((el) => el.lastModified === file.lastModified),
		);
		const compressed = await Promise.all(fresh.map(compressImage));
		const next = [...files, ...compressed];

		setFiles(next);
		showPreviews(next);
	};

	const handleClose = () => {
		form.reset();
		setFiles([]);
		setPreviews([]);
		onClose();
	};

	const { mutate, isPending } = useMutation({
		mutationFn: bookingApi.reportPlaceWasTaken,
		onSuccess: () => {
			void useShowNotifaction({ text: 'Обращение отправлено', type: 'success' });
			handleClose();
		},
		onError: (error: AxiosError<IRequestError>) => {
			const text =
				error.response?.data?.message ??
				(error.response
					? `Не удалось отправить обращение (ошибка ${error.response.status})`
					: 'Не удалось отправить обращение. Проверьте соединение и размер фото');
			void useShowNotifaction({ text, type: 'error' });
		},
	});

	const buttons = [
		{
			children: 'Отправить',
			...(form.isValid()
				? {
						loading: isPending,
						onClick: form.onSubmit((values) => {
							const formData = new FormData();

							formData.set('Fullname', fullname ?? '');
							if (selection.manualPlace) {
								formData.set('ManualPlace', selection.manualPlace);
							} else if (selection.bookingId) {
								formData.set('BookingId', String(selection.bookingId));
							} else if (selection.bookingObjectId) {
								formData.set('BookingObjectId', String(selection.bookingObjectId));
							}
							formData.set('CarNumber', values.carNumber);
							formData.set('UserTextVariant', values.subject.trim() || SUBJECT);
							files.forEach((file, idx) => {
								formData.set(`${Date.now()}-${idx}`, file);
							});

							mutate(formData);
						}),
					}
				: { variant: 'light' }),
		},
	];

	return (
		<BottomSheet
			opened={opened}
			onClose={onBack}
			title="Обращение"
			withBackButton
			withCloseButton={false}
			buttons={buttons}
		>
			<Container p={0} pb="md">
				<form>
					<Group>
						<LabelInput name="subject" label="Тема" {...form.getInputProps('subject')} multiple />

						<CarInput
							onChange={(carNumber) =>
								form.setFieldValue('carNumber', String(carNumber).toUpperCase())
							}
							error={form.errors.carNumber}
						/>

						{previews.length > 0 && (
							<ScrollArea.Autosize mah={160} w="100%">
								<Flex gap="xs" wrap="wrap" pr={15}>
									{previews.map((preview) => (
										<Box w={70} h={70} mt={5} pos="relative" key={preview.lastModified}>
											<Image h="100%" src={preview.img} radius={10} />
											<CloseButton
												pos="absolute"
												size="xs"
												right={-5}
												top={-5}
												bg="#F3F4F8"
												onClick={() => removeImage(preview.lastModified)}
											/>
										</Box>
									))}
								</Flex>
							</ScrollArea.Autosize>
						)}

						<FileButton
							resetRef={resetRef}
							onChange={changeHandler}
							accept="image/png,image/jpeg"
							multiple
						>
							{(props) => (
								<Button variant="default" fullWidth {...props}>
									<Group>
										<IconPhoto />
										<Text>Добавить фото</Text>
									</Group>
								</Button>
							)}
						</FileButton>
					</Group>
				</form>
			</Container>
		</BottomSheet>
	);
};
